import type { CSSProperties } from "react"

const DEFAULT_BG = "#ED1B2E"
const DARK_TEXT = "#111827"
const LIGHT_TEXT = "#ffffff"

function parseColorToRgb(input: string): { r: number; g: number; b: number } | null {
  const s = input.trim()
  const lower = s.toLowerCase()
  if (lower === "white") return { r: 255, g: 255, b: 255 }
  if (lower === "black") return { r: 0, g: 0, b: 0 }

  // Alpha is dropped from #RRGGBBAA: callers layer their own transparency on top.
  const hex6 = /^#([0-9a-f]{6})(?:[0-9a-f]{2})?$/i.exec(s)
  if (hex6) {
    const n = parseInt(hex6[1], 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }
  const hex3 = /^#([0-9a-f]{3})(?:[0-9a-f])?$/i.exec(s)
  if (hex3) {
    const h = hex3[1]
    return {
      r: parseInt(h[0] + h[0], 16),
      g: parseInt(h[1] + h[1], 16),
      b: parseInt(h[2] + h[2], 16),
    }
  }

  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s)
  if (rgb) {
    const clamp = (n: number) => Math.max(0, Math.min(255, n))
    return { r: clamp(+rgb[1]), g: clamp(+rgb[2]), b: clamp(+rgb[3]) }
  }

  return null
}

function toHex6(rgb: { r: number; g: number; b: number }): string {
  const part = (n: number) => n.toString(16).padStart(2, "0")
  return `#${part(rgb.r)}${part(rgb.g)}${part(rgb.b)}`
}

function perceivedBrightness(rgb: { r: number; g: number; b: number }): number {
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
}

/**
 * A tenant colour normalised to `#RRGGBB`, falling back to the brand default when it is
 * unset or unreadable.
 *
 * The exact-six-digit shape is a contract, not a cosmetic detail: callers build tints by
 * appending a hex alpha byte (`${themeColor}0d`). A shorthand like `#25f`, a bare `red`,
 * or an empty string concatenates into nonsense, and because one bad layer invalidates a
 * whole `background`/`background-image` declaration, the browser drops the entire thing —
 * which silently takes cover photos and section tints with it. The colour picker in the
 * admin lets any text through, so tenants really do store these values.
 */
export function resolveThemeColor(color?: string | null): string {
  const rgb = color ? parseColorToRgb(color) : null
  return rgb ? toHex6(rgb) : DEFAULT_BG
}

/**
 * As `resolveThemeColor`, but keeps "unset" distinguishable from "default" for colours
 * that are meant to be optional (e.g. `serviceColor`, where no value means no tint at all
 * rather than a brand-red one). Unreadable values are treated as unset.
 */
export function resolveOptionalColor(color?: string | null): string | undefined {
  const rgb = color ? parseColorToRgb(color) : null
  return rgb ? toHex6(rgb) : undefined
}

/**
 * Solid primary buttons that use `project.buttonColor` (or theme) must not assume
 * white label text — admins may set `#FFFFFF` or `white`, which would be invisible.
 */
export function getReadablePrimaryButtonStyle(color?: string | null): CSSProperties {
  const bg = resolveThemeColor(color)
  const rgb = parseColorToRgb(bg)!
  return {
    backgroundColor: bg,
    color: perceivedBrightness(rgb) > 186 ? DARK_TEXT : LIGHT_TEXT,
  }
}
